import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import DataTable from "react-data-table-component";
import { userRequest } from "../../utils/requestMethods";
import { Header, CustomPagination, SkeletonTable } from "../../components";
import { useStateContext } from "../../contexts/ContextProvider";
import { toast } from "react-toastify";
import { FiUsers, FiCheckSquare, FiAward } from "react-icons/fi";
import * as Icons from "../../data/class";
import { useClassGroups } from "../../hooks/useClassGroups";
import * as api from "../../utils/classGroupApi";
import { splitArmName } from "../../utils/classGroupHelpers";

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      fontWeight: "600",
      fontSize: "14px",
      color: "#475569",
      borderBottom: "2px solid #e2e8f0",
    },
  },
  headCells: {
    style: {
      paddingLeft: "20px",
      paddingRight: "20px",
      paddingTop: "16px",
      paddingBottom: "16px",
      textTransform: "none",
      letterSpacing: "0",
    },
  },
  cells: {
    style: {
      paddingLeft: "20px",
      paddingRight: "20px",
      paddingTop: "12px",
      paddingBottom: "12px",
    },
  },
  rows: {
    style: {
      borderBottom: "1px solid #f1f5f9",
      "&:hover": {
        backgroundColor: "#f8fafc",
      },
    },
  },
};

const TeacherClassManagement = () => {
  const history = useHistory();
  const { user, school } = useStateContext();
  const { classGroups, isEnabled: isGroupingEnabled } = useClassGroups();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const teacherId = user?._id;
  const schoolId = school?._id;

  const fetchTeacherClasses = async () => {
    if (!teacherId || !schoolId) return;

    try {
      setLoading(true);
      const { data } = await userRequest.get(
        `/teachers/teacher-assigned-classes/${schoolId}/${teacherId}?limit=50`,
      );
      setClasses(data.classes || []);
    } catch (error) {
      console.error("Error fetching teacher classes:", error);
      toast.error("Failed to load classes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeacherClasses();
  }, [teacherId, schoolId]);

  // Each class is listed on its own; a class that's one arm of a class level just gets a
  // "Part of <level>" tag. Roles differ per arm, so they're never merged.
  const groupNameFor = (cls) => {
    const groupId = isGroupingEnabled ? api.getClassGroupId(cls.class_group_id) : null;
    if (!groupId) return null;
    return (
      (classGroups || []).find((g) => String(g._id) === String(groupId))?.name ||
      splitArmName(cls.class_name)?.base ||
      null
    );
  };

  const filteredClasses = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return classes;

    return classes.filter((cls) =>
      [cls.class_name, cls.level_of_education, groupNameFor(cls)].some((value) =>
        value?.toLowerCase().includes(query),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, searchText, classGroups, isGroupingEnabled]);

  const columns = [
    {
      name: "Class Name",
      selector: (row) => (
        <div className="py-2">
          <div className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
            <span>{row.class_name}</span>
            {groupNameFor(row) && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium border border-indigo-200">
                Part of {groupNameFor(row)}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 capitalize">{row.level_of_education}</div>
        </div>
      ),
    },
    {
      name: "Level of Education",
      selector: (row) => (
        <div className="py-2">
          <div className="font-medium text-slate-700 capitalize text-xs">
            {row.level_of_education || "N/A"}
          </div>
        </div>
      ),
    },
    {
      name: "Students",
      selector: (row) => (
        <div className="py-2 flex items-center">
          <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center mr-2.5">
            <FiUsers className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <div className="font-bold text-slate-800 text-sm">{row.total_students || 0}</div>
            <div className="text-[11px] text-slate-500">Students</div>
          </div>
        </div>
      ),
    },
    {
      name: "Role",
      selector: (row) => {
        const role = row.teacher_role || "N/A";

        return (
          <div className="py-2">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                role === "primary"
                  ? "bg-indigo-100 text-indigo-800"
                  : role === "assistant"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-gray-100 text-gray-800"
              }`}
            >
              {role === "primary"
                ? "Main class teacher"
                : role === "assistant"
                  ? "Assistant class teacher"
                  : "Teacher"}
            </span>
          </div>
        );
      },
      width: "190px",
      minWidth: "170px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1.5 py-2">
          <button
            onClick={() => history.push(`/t/students/${row._id}`)}
            className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-all duration-200 shadow-2xs"
            title="View Students"
          >
            <FiUsers className="w-3.5 h-3.5 mr-1" />
            Students
          </button>
          <button
            onClick={() => history.push(`/t/attendance/mark/${row._id}`)}
            className="inline-flex items-center px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-lg transition-all duration-200 border border-indigo-200"
            title="Mark Attendance"
          >
            <FiCheckSquare className="w-3.5 h-3.5 mr-1" />
            Attendance
          </button>
          <button
            onClick={() => history.push(`/t/results/students/${row._id}`)}
            className="inline-flex items-center px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold rounded-lg transition-all duration-200 border border-purple-200"
            title="Upload Results"
          >
            <FiAward className="w-3.5 h-3.5 mr-1" />
            Results
          </button>
        </div>
      ),
      width: "320px",
      minWidth: "300px",
      ignoreRowClick: true,
    },
  ];

  // Calculate statistics
  const totalClasses = classes.length;
  const totalStudents = classes.reduce((sum, cls) => sum + (cls.total_students || 0), 0);

  return (
    <>
      <div className="px-4 md:px-10 pt-4 md:pt-6 mt-20 md:mt-0">
        <Header category="Teacher Module" title="Class Management" />
      </div>

      {/* Statistics Cards */}
      <div className="px-3 md:px-10 pt-2 md:pt-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-xs md:text-sm mb-1">Total Classes</p>
                <p className="text-2xl md:text-3xl font-bold text-slate-800">{totalClasses}</p>
              </div>
              <div className="w-10 h-10 md:w-14 md:h-14 bg-indigo-800 rounded-xl flex items-center justify-center">
                <img src={Icons.userMultiple} alt="Classes" className="w-5 h-5 md:w-7 md:h-7" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-xs md:text-sm mb-1">Total Students</p>
                <p className="text-2xl md:text-3xl font-bold text-slate-800">{totalStudents}</p>
                <p className="text-xs text-slate-400 mt-1">Across all assigned classes</p>
              </div>
              <div className="w-10 h-10 md:w-14 md:h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <FiUsers className="w-5 h-5 md:w-7 md:h-7 text-green-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="m-2 md:m-10 mt-3 md:mt-8 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-1">My Classes</h2>
            <p className="text-sm text-slate-500">
              Classes you're the class teacher of, and their students
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder="Search classes..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-2xs transition-all duration-200"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-4 w-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* All Classes Table */}
        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs bg-white">
          <div className="overflow-x-auto">
            <div className="min-w-full" style={{ minWidth: "800px" }}>
              <DataTable
                data={filteredClasses}
                columns={columns}
                getRowId={(row) => row._id}
                pagination
                paginationComponent={CustomPagination}
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                customStyles={customStyles}
                progressPending={loading}
                progressComponent={<SkeletonTable rows={5} columns={5} />}
                highlightOnHover={true}
                striped={true}
                noDataComponent={
                  <div className="text-center py-16">
                    <div className="mx-auto h-24 w-24 text-slate-300 mb-6 flex items-center justify-center">
                      <img
                        src={Icons.userMultiple}
                        alt="No Classes"
                        className="w-16 h-16 opacity-50"
                      />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">
                      No classes assigned
                    </h3>
                    <p className="text-slate-500 text-sm">
                      {searchText
                        ? "No classes match your search criteria"
                        : "You have not been assigned to any classes yet"}
                    </p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeacherClassManagement;
